(() => {
  const configNode = document.getElementById("nma-favorites-config");
  if (!configNode) {
    return;
  }

  let config;
  try {
    config = JSON.parse(configNode.textContent || "{}");
  } catch {
    return;
  }

  const root = document.querySelector("[data-nma-favorites-page]");
  if (!root) {
    return;
  }

  const grid = root.querySelector("[data-nma-favorites-grid]");
  const count = root.querySelector("[data-nma-favorites-count]");
  const status = root.querySelector("[data-nma-favorites-status]");
  const loadWrap = root.querySelector(".nma-favorites-load");
  const loadMore = root.querySelector("[data-nma-favorites-load-more]");
  const pager = root.querySelector("[data-nma-favorites-pager]");
  const guest = root.querySelector("[data-nma-favorites-guest]");
  const recovery = root.querySelector("[data-nma-favorites-recovery]");

  if (!grid) {
    return;
  }

  const gridSettings = config.grid || {};
  const paginationModes = ["load_more", "infinite", "pagination"];
  const rootPagination = root.dataset.pagination || "";
  const rootPerPage = Number(root.dataset.perPage);
  const mode = paginationModes.includes(rootPagination)
    ? rootPagination
    : paginationModes.includes(gridSettings.pagination)
      ? gridSettings.pagination
      : "load_more";
  const perPage =
    Number.isFinite(rootPerPage) && rootPerPage > 0
      ? Math.max(2, rootPerPage)
      : Number.isFinite(gridSettings.perPage)
        ? Math.max(2, gridSettings.perPage)
        : 12;

  let visible = perPage;
  let page = 1;
  let generation = 0;
  let subscribed = false;
  let renderedOrder = "";
  const SKELETON_COUNT = Math.max(4, perPage);
  const controllers = new Set();
  const cardCache = new Map();
  const handleCache = new Map();
  const pendingFetches = new Map();

  function safeText(value) {
    return document.createTextNode(String(value ?? ""));
  }

  function joinRoot(path) {
    const rootUrl = config.rootUrl || "/";
    if (rootUrl === "/") {
      return path;
    }
    return `${rootUrl.replace(/\/$/, "")}${path}`;
  }

  function placeholder(productId, index) {
    const li = document.createElement("li");
    li.dataset.productId = productId;
    li.dataset.slotId = productId;
    li.dataset.index = String(index);
    li.className = "nma-favorites-card-slot is-loading";
    return li;
  }

  function slotFor(productId) {
    return grid.querySelector(`li[data-slot-id="${productId}"]`);
  }

  function showUnavailable(productId) {
    const li = slotFor(productId);
    if (!li) {
      return;
    }
    li.className = "nma-favorites-card-slot";
    li.replaceChildren(
      Object.assign(document.createElement("p"), {
        textContent:
          config.strings?.unavailable || "This product is currently unavailable.",
      }),
    );
  }

  function paintSkeletons(total) {
    grid.setAttribute("aria-busy", "true");
    grid.replaceChildren();
    for (let index = 0; index < total; index += 1) {
      const li = document.createElement("li");
      li.className = "nma-favorites-card-slot is-loading";
      li.setAttribute("aria-hidden", "true");
      grid.appendChild(li);
    }
  }

  function setLoadingStatus(message) {
    if (!status) {
      return;
    }
    if (message) {
      status.setAttribute("data-state", "loading");
      status.replaceChildren(safeText(message));
    } else {
      status.removeAttribute("data-state");
      status.replaceChildren();
    }
  }

  function rebuildGrid(ids) {
    grid.replaceChildren();
    ids.forEach((productId, index) => {
      const cached = cardCache.get(productId);
      const li = placeholder(productId, index);
      if (cached) {
        li.className = "";
        li.replaceChildren(cached);
      }
      grid.appendChild(li);
    });
  }

  async function fetchOne(productId, generationAtStart) {
    const handle = handleCache.get(productId);
    if (!handle) {
      showUnavailable(productId);
      return;
    }

    if (cardCache.has(productId)) {
      return;
    }
    const pending = pendingFetches.get(productId);
    if (pending) {
      await pending.catch(() => {});
      return;
    }

    const controller = new AbortController();
    controllers.add(controller);

    const task = (async () => {
      const url = joinRoot(
        `/products/${encodeURIComponent(handle)}?section_id=nma-favorite-card`,
      );
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "text/html" },
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error("card request failed");
      }
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, "text/html");
      const wrapper = doc.querySelector("[data-nma-favorite-card]");
      if (
        !wrapper ||
        wrapper.getAttribute("data-product-id") !== String(productId)
      ) {
        throw new Error("unexpected card payload");
      }
      wrapper.querySelectorAll("script").forEach((script) => script.remove());
      return wrapper;
    })();

    pendingFetches.set(productId, task);
    try {
      const wrapper = await task;
      cardCache.set(productId, wrapper);
      if (generationAtStart !== generation) {
        return;
      }
      const li = slotFor(productId);
      if (li) {
        li.className = "";
        li.replaceChildren(wrapper);
      }
    } catch {
      if (generationAtStart !== generation) {
        return;
      }
      showUnavailable(productId);
    } finally {
      pendingFetches.delete(productId);
      controllers.delete(controller);
    }
  }

  function scrollToGrid() {
    if (typeof window === "undefined" || typeof window.scrollTo !== "function") {
      return;
    }
    window.scrollTo({ top: root.offsetTop, behavior: "smooth" });
  }

  function renderPager(snapshot) {
    if (!pager) {
      return;
    }
    const totalPages = Math.max(1, Math.ceil(snapshot.count / perPage));
    if (mode !== "pagination" || totalPages <= 1) {
      pager.hidden = true;
      pager.replaceChildren();
      return;
    }
    pager.hidden = false;
    pager.replaceChildren();

    const previous = document.createElement("button");
    previous.type = "button";
    previous.className = "button button--secondary";
    previous.textContent = config.strings?.previous || "Previous";
    previous.disabled = page <= 1;
    previous.addEventListener("click", () => {
      page = Math.max(1, page - 1);
      scrollToGrid();
      render();
    });
    pager.appendChild(previous);

    const label = document.createElement("span");
    label.className = "nma-favorites-pager__label";
    label.textContent = `${page} / ${totalPages}`;
    pager.appendChild(label);

    const next = document.createElement("button");
    next.type = "button";
    next.className = "button button--secondary";
    next.textContent = config.strings?.next || "Next";
    next.disabled = page >= totalPages;
    next.addEventListener("click", () => {
      page = Math.min(totalPages, page + 1);
      scrollToGrid();
      render();
    });
    pager.appendChild(next);
  }

  function updateControls(snapshot) {
    if (mode === "load_more") {
      if (loadMore) {
        loadMore.hidden = snapshot.count <= visible;
      }
      if (pager) {
        pager.hidden = true;
      }
      return;
    }
    if (loadMore) {
      loadMore.hidden = true;
    }
    if (mode === "pagination") {
      renderPager(snapshot);
    } else if (pager) {
      pager.hidden = true;
    }
  }

  async function render() {
    generation += 1;
    const currentGeneration = generation;

    const manager = globalThis.NMAFavorites?.getManager?.();
    if (!manager) {
      return;
    }

    const snapshot = manager.getSnapshot();

    if (snapshot.identity === "unknown") {
      if (renderedOrder !== "__loading__") {
        renderedOrder = "__loading__";
        paintSkeletons(SKELETON_COUNT);
      }
      if (count) {
        count.replaceChildren(safeText(config.strings?.loading ?? ""));
      }
      if (guest) {
        guest.hidden = true;
      }
      setLoadingStatus(config.strings?.loading ?? "Loading favorites");
      updateControls({ count: 0 });
      return;
    }

    grid.removeAttribute("aria-busy");
    if (count) {
      count.replaceChildren(
        safeText(
          (config.strings?.count_label ?? "Favorites ({{ count }})").replace(
            "{{ count }}",
            String(snapshot.count),
          ),
        ),
      );
    }
    if (guest) {
      guest.hidden = snapshot.identity !== "guest";
    }
    setLoadingStatus(
      snapshot.identity !== "guest" && snapshot.count === 0
        ? config.strings?.empty_title || ""
        : "",
    );

    for (const item of snapshot.items) {
      if (item.handle) {
        handleCache.set(String(item.productId), item.handle);
      }
    }

    let ids;
    if (mode === "pagination") {
      const totalPages = Math.max(1, Math.ceil(snapshot.count / perPage));
      page = Math.min(Math.max(1, page), totalPages);
      const start = (page - 1) * perPage;
      ids = snapshot.items
        .slice(start, start + perPage)
        .map((item) => String(item.productId));
    } else {
      ids = snapshot.items
        .slice(0, visible)
        .map((item) => String(item.productId));
    }

    const missingHandles = ids.filter((id) => !handleCache.has(id));
    if (snapshot.identity === "account" && missingHandles.length > 0) {
      const resolved = await manager.resolveHandles(missingHandles.slice(0, 12));
      for (const entry of resolved || []) {
        if (entry?.handle) {
          handleCache.set(String(entry.productId), entry.handle);
        }
      }
      if (currentGeneration !== generation) {
        return;
      }
    }

    const orderKey = ids.join(",");
    if (orderKey !== renderedOrder) {
      for (const controller of controllers) {
        controller.abort();
      }
      controllers.clear();
      rebuildGrid(ids);
      renderedOrder = orderKey;
    }

    const tasks = ids
      .filter((productId) => !cardCache.has(productId))
      .map((productId) => () => fetchOne(productId, currentGeneration));

    let nextIndex = 0;
    const workers = Array.from(
      { length: Math.min(4, tasks.length) },
      async () => {
        while (nextIndex < tasks.length) {
          const taskIndex = nextIndex;
          nextIndex += 1;
          await tasks[taskIndex]();
        }
      },
    );
    await Promise.all(workers);

    updateControls(snapshot);
    renderRecovery(snapshot, manager);
  }

  function renderRecovery(snapshot, manager) {
    if (!recovery) {
      return;
    }
    recovery.hidden = !snapshot.guestImport && !snapshot.needsReview;
    if (snapshot.guestImport) {
      recovery.innerHTML = "";
      const title = document.createElement("h2");
      title.textContent =
        config.strings?.import_title || "Products saved on this device";
      recovery.appendChild(title);
      const list = document.createElement("div");
      list.className = "nma-favorites-recovery__list";
      for (const item of snapshot.guestImport.items || []) {
        const row = document.createElement("label");
        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.value = item.productId;
        checkbox.dataset.productId = item.productId;
        row.appendChild(checkbox);
        row.appendChild(safeText(item.productId));
        list.appendChild(row);
      }
      recovery.appendChild(list);
      const save = document.createElement("button");
      save.type = "button";
      save.className = "button";
      save.textContent =
        config.strings?.save_selected || "Save selected products";
      save.addEventListener("click", () => {
        const selected = [...list.querySelectorAll("input:checked")].map(
          (input) => input.value,
        );
        manager.importSelected(selected);
      });
      recovery.appendChild(save);
    }
    if (snapshot.needsReview) {
      const review = document.createElement("div");
      review.className = "nma-favorites-recovery__review";
      const message = document.createElement("p");
      message.textContent =
        config.strings?.needs_review || "Some changes could not be confirmed.";
      review.appendChild(message);
      const retry = document.createElement("button");
      retry.type = "button";
      retry.className = "button";
      retry.textContent =
        config.strings?.retry_changes || "Save these changes again";
      retry.addEventListener("click", () => manager.retryNeedsReview());
      review.appendChild(retry);
      const discard = document.createElement("button");
      discard.type = "button";
      discard.className = "button button--secondary";
      discard.textContent =
        config.strings?.discard_changes || "Discard pending changes";
      discard.addEventListener("click", () => {
        if (
          window.confirm(
            config.strings?.discard_changes || "Discard pending changes?",
          )
        ) {
          manager.discardNeedsReview();
        }
      });
      review.appendChild(discard);
      recovery.appendChild(review);
    }
  }

  if (loadMore) {
    loadMore.addEventListener("click", () => {
      visible += perPage;
      render();
    });
  }

  if (mode === "infinite" && loadWrap && "IntersectionObserver" in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }
          const snapshot =
            globalThis.NMAFavorites?.getManager?.()?.getSnapshot?.();
          if (snapshot && snapshot.count > visible) {
            visible += perPage;
            render();
          }
        }
      },
      { rootMargin: "240px" },
    );
    observer.observe(loadWrap);
  }

  // Paint the loading state before the manager resolves so the grid never
  // looks empty while identity and product cards are being fetched.
  paintSkeletons(SKELETON_COUNT);
  setLoadingStatus(config.strings?.loading ?? "Loading favorites");
  updateControls({ count: 0 });

  function attach(manager) {
    if (subscribed) {
      return;
    }
    subscribed = true;
    manager.subscribe?.(() => {
      render();
    });
    render();
  }

  const existingManager = globalThis.NMAFavorites?.getManager?.();
  if (existingManager) {
    attach(existingManager);
  } else {
    document.addEventListener(
      "nma-favorites:manager-ready",
      () => {
        const ready = globalThis.NMAFavorites?.getManager?.();
        if (ready) {
          attach(ready);
        }
      },
      { once: true },
    );
  }
})();
